import { Box } from '@mui/material';

import ControlPanel from '@/cam-admin/control-panel/ControlPanel';
import NotificationsPage from '@/cam-admin/notifications/NotificationsPage';
import NotificationsList from '@/cam-admin/notifications/NotificationsList';
import NotificationStack from '@/cam-admin/notifications/NotificationStack';
import { CAM_NOTIFICATIONS } from '@/cam-admin/notifications/Notifications.fixtures';
import { notificationSceneById } from '@/cam-admin/notifications/Notifications.scenes';
import { type HandoverSpec } from '@/designs-dev/handover/types';

const noop = () => {};

// The feed in context: the control panel with the notifications overlay open
// (the bell opens it over the whole shell), seeded into a catalog scene so the
// live state select and this handover share one source of state.
const scene = (id: string) => (
  <ControlPanel
    embedded
    initialView='notifications'
    notificationsInitial={notificationSceneById(id).initial}
  />
);

// Where a notification lands: the customer's control panel on the trigger's tab.
const landed = (initialTab: 'actions' | 'care-search' | 'documents') => (
  <ControlPanel embedded initialTab={initialTab} />
);

// The feed on its own (for the empty state), framed on the panel surface.
const feed = (items: typeof CAM_NOTIFICATIONS) => (
  <Box sx={{ height: '100%', p: 3, bgcolor: 'background.default' }}>
    <NotificationsPage items={items} onOpen={noop} />
  </Box>
);

// The CAM-facing notifications handover (Care Recipient Control Panel V3). A
// single global feed of recent customer activity, opened from the bell as a
// full-bleed overlay. Covers the three V3 triggers — a carer completing an
// action, responding to a published shortlist, and uploading a key document —
// each deep-linking to that customer's control panel. CAM-facing only.
export const notificationsSpec: HandoverSpec = {
  id: 'notifications',
  title: 'Notifications',
  screen: 'desktop',
  description:
    'A single global feed showing recent customer activity across every care circle, opened from the bell in the control-panel app bar as a full-bleed overlay. V3 covers three triggers — a carer completing an action, responding to a published shortlist, and uploading a key document — each deep-linking to that customer’s control panel. CAM-facing only; the carer-side notifications stay in the slices that generate them.',
  links: [
    {
      label: 'Mini PRD: Notifications (Linear)',
      url: 'https://linear.app/karehero/document/mini-prd-notifications-1a60deef1d4d',
    },
    {
      label: 'Feature spec: Notifications (CAM admin) (Linear)',
      url: 'https://linear.app/karehero/document/feature-spec-notifications-cam-admin-c94d5bb74a58',
    },
    { label: 'Live prototype (bell → feed)', url: '/care-search' },
    { label: 'Notifications page (direct)', url: '/care-search/notifications' },
    { label: 'Actions trigger (handover)', url: '/handover/actions' },
    { label: 'Documents trigger (handover)', url: '/handover/documents' },
  ],
  tables: [
    {
      title: 'NOTIFICATION SET',
      caption:
        'The notifications this handover covers. All in-app only for V3 (no email / Slack / push). Each is a carer action surfacing to the CAM and deep-links to that customer’s control panel.',
      columns: ['Type', 'Trigger', 'What it says', 'Deep-links to', 'In V3?'],
      rows: [
        [
          'Action',
          'Carer marks a CAM-set action complete',
          '“{Carer} completed ‘{Action title}’”',
          'Actions tab',
          '✓ Covered',
        ],
        [
          'Shortlist',
          'Carer secures a provider on a published shortlist',
          '“{Carer} secured {Provider}”',
          'Care search tab',
          '✓ Covered',
        ],
        [
          'Shortlist',
          'Carer comments on a provider',
          '“{Carer} commented on {Provider}”',
          'Care search tab',
          '✓ Covered',
        ],
        [
          'Document',
          'Carer uploads a key document',
          '“{Carer} uploaded ‘{Document name}’”',
          'Documents tab',
          '✓ Covered',
        ],
        [
          'Onboarding',
          'New customer signs up / completes onboarding',
          '“{Customer} completed onboarding”',
          'Overview tab',
          'Stretch — not in V3',
        ],
        [
          'System',
          'A CAM-set action goes overdue',
          '(copy TBD)',
          'Actions tab',
          'Out of scope — system event, not a customer action',
        ],
      ],
    },
  ],
  flows: [
    {
      title: 'Read the feed and act',
      steps: [
        {
          id: 'feed',
          title: 'Feed',
          tap: 'Tap the bell in the app bar',
          render: () => scene('feed'),
        },
        {
          id: 'open',
          title: 'Open a notification',
          tap: 'Tap a row — it marks read, then deep-links to that customer',
          render: () => landed('actions'),
        },
      ],
    },
  ],
  states: [
    {
      id: 'feed',
      title: 'Feed',
      kind: 'happy',
      repro:
        'Bell → feed. Seeded activity across several customers, grouped Today / This week / Earlier',
      render: () => scene('feed'),
    },
    {
      id: 'unread',
      title: 'Unread only',
      kind: 'happy',
      repro: 'Toggle the Unread filter — only the rows the CAM hasn’t read',
      render: () => scene('feed'),
    },
    {
      id: 'all-read',
      title: 'All caught up',
      kind: 'happy',
      repro: 'Every notification read — the bell badge clears, Unread is empty',
      render: () => scene('all-read'),
    },
    {
      id: 'empty',
      title: 'No activity yet',
      kind: 'empty',
      repro:
        'Feed with no items → “No activity yet. Customer updates will show up here.” (Unread with nothing unread reads “You’re all caught up.”)',
      render: () => scene('empty'),
    },
  ],
  requirements: [
    {
      text: 'Feed opens from the bell as a full-bleed overlay over the control panel; Back returns you exactly where you were',
      status: 'built',
      where: 'App-bar bell → notifications overlay',
    },
    {
      text: 'Trigger — carer completes an action → row reads “{Carer} completed ‘{Action title}’”, deep-links to the Actions tab',
      status: 'built',
      where: 'Feed (type: action — check-circle, warm orange)',
    },
    {
      text: 'Trigger — carer responds to a published shortlist → “{Carer} secured {Provider}” (secures one) or “{Carer} commented on {Provider}” (comments), deep-links to the Care search tab',
      status: 'built',
      where: 'Feed (type: shortlist — magnifying-glass, lime green)',
      note: 'Only meaningful responses fire — secured or a comment. Plain status changes (Contacted, Unavailable) don’t.',
    },
    {
      text: 'Trigger — carer uploads a key document → “{Carer} uploaded ‘{Document name}’”, deep-links to the Documents tab',
      status: 'built',
      where: 'Feed (type: document — file-lines, cherry)',
    },
    {
      text: 'Every row carries the customer line (“Caring for {name}”) — it’s a global feed across all care recipients',
      status: 'built',
      where: 'Feed rows',
    },
    {
      text: 'Recency chunks: Today / This week / Earlier, newest first',
      status: 'built',
      where: 'NotificationsList',
    },
    {
      text: 'All / Unread filter',
      status: 'built',
      where: 'Feed header (ToggleButtonGroup)',
    },
    {
      text: 'Per-row read/unread (envelope toggle) + Mark all as read; unread count badge on the bell',
      status: 'built',
      where: 'Feed rows + header + app-bar bell',
      note: 'Read state is local prototype state — see the per-CAM open question below',
    },
    {
      text: 'Open a notification → marks it read, then deep-links to that customer’s control panel on the trigger’s tab',
      status: 'partial',
      where: 'Tap any row',
      note: 'Lands on the right customer (header switches) and the right tab. Item-level precision (the exact action / provider / document) is an open question — the panel supports tab+id intents, so it’s likely possible.',
    },
    {
      text: 'New customer signs up / completes onboarding → notify the CAM',
      status: 'not-built',
      note: 'Stretch in the mini-PRD — the trigger that lets a CAM live entirely in the control panel',
    },
    {
      text: 'Filter the feed by customer or activity type',
      status: 'not-built',
      note: 'Stretch in the mini-PRD',
    },
    {
      text: 'Overdue-action notification',
      status: 'out-of-scope',
      note: 'A system/time event, not a customer action — out of V3’s remit',
    },
    {
      text: 'Per-CAM read / dismiss state',
      status: 'not-built',
      note: 'Open question: needs a logged-in CAM identity, which the control panel doesn’t model yet. The prototype’s read state is shared/local.',
    },
    {
      text: 'Email / Slack / push notifications',
      status: 'out-of-scope',
      note: 'In-app only for V3; push depends on separate infra',
    },
    {
      text: 'Attio sync and customer-facing notifications',
      status: 'out-of-scope',
      note: 'CAM-facing only; the carer-side notifications stay in the slices (Actions, Call notes, Shortlists)',
    },
    {
      text: 'Backend: the events that fire these notifications, read-state storage, real-time delivery',
      status: 'out-of-scope',
      note: 'Backend — out of scope for this design handover',
    },
  ],
  components: {
    mui: [
      'AppBar',
      'Badge',
      'Box',
      'Button',
      'Card',
      'IconButton',
      'Stack',
      'ToggleButtonGroup',
      'Typography',
    ],
    existing: [
      'Icon (llama)',
      'NotificationRow',
      'Body',
      'Sidebar',
      'Layout',
      'BackLink',
      'GroupLabel',
    ],
    new: ['NotificationsPage', 'NotificationsList', 'NotificationStack'],
  },
  componentsNote:
    'NotificationsPage owns the feed (All/Unread filter, empty states, Mark all as read); NotificationsList groups items into recency chunks; NotificationStack renders a chunk’s rows on the shared NotificationRow — the same row the member-app Notification Centre uses, so both surfaces read the same.',
  componentPreviews: {
    NotificationsPage: () => feed(CAM_NOTIFICATIONS),
    NotificationsList: () => (
      <Box sx={{ p: 2, bgcolor: 'background.default' }}>
        <NotificationsList
          items={CAM_NOTIFICATIONS}
          onOpen={noop}
          onToggleRead={noop}
        />
      </Box>
    ),
    NotificationStack: () => (
      <Box sx={{ p: 2, bgcolor: 'background.default' }}>
        <NotificationStack
          items={CAM_NOTIFICATIONS.slice(0, 3)}
          onOpen={noop}
          onToggleRead={noop}
        />
      </Box>
    ),
  },
};
